import { Injectable, OnDestroy } from "@angular/core";
import * as dayjs from "dayjs";
import { BehaviorSubject, combineLatest, debounceTime, filter, map, merge, Subject, takeUntil, tap } from "rxjs";
import { LogService } from "../baseServices/logging/log.service";
import { DateTimeHelper, DateTimeType } from "../utils/date-time-helper";
import { ActivityLogService } from "./activityLog.service";
import { AppUserService } from "./app-user.service";
import { CurrentReviewPeriodService } from "./current-review-period.service";
import { TimeService } from "./time.service";
import { TimeRecordService } from "./timeRecord.service";

export interface DailyTimeStatus {
  date: dayjs.Dayjs;
  hasReturnedTime: boolean;
  hasUnsubmittedTime: boolean;
}

/**
 * Maintains and exposes a sorted array of DailyTimeStatus for the current user within the active review period.
 * Automatically refreshes on user/review period changes and relevant TimeRecord/ActivityLog updates.
 */
@Injectable({ providedIn: "root" })
export class AppUserDailyTimeStatusService implements OnDestroy {
  private readonly dailyTimeStatusesSubject = new BehaviorSubject<DailyTimeStatus[]>([]);
  /** 
   * Emits the current array of `DailyTimeStatus` objects - one entry per day in the active review period.
   * Always sorted ascending by date.
   * Guarantees that the dates are sorted in ascending order.
   * Refreshes on user change, review period change, and current user's time data changes.
   */
  public readonly dailyTimeStatuses$ = this.dailyTimeStatusesSubject.asObservable();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private logService: LogService,
    private appUserService: AppUserService,
    private timeRecordService: TimeRecordService,
    private activityLogService: ActivityLogService,
    private timeService: TimeService,
    private currentReviewPeriodService: CurrentReviewPeriodService,
  ) {
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSubscriptions(): void {
    // On user change (login/logout) or review period change
    combineLatest([
      this.appUserService.currentUserId$,
      this.currentReviewPeriodService.currentReviewPeriod$
    ])
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(50) // avoid rapid-fire changes during login bootstrap
      )
      .subscribe(([userId, period]) => this.refreshAllDailyTimeStatuses());

    // On ActivityLog or TimeRecord change for this user
    const timeRecordChanges$ = this.timeRecordService.changeEmitted$.pipe(
      takeUntil(this.destroy$),
      filter(e => e.data.EmployeeId === this.appUserService.currentUserId),
      map(e => e.data.Date)
    );
    const activityLogChanges$ = this.activityLogService.changeEmitted$.pipe(
      takeUntil(this.destroy$),
      filter(e => e.data.UserId === this.appUserService.currentUserId),
      map(e => e.data.Date)
    );
    merge(timeRecordChanges$, activityLogChanges$)
      .pipe(debounceTime(50)) // Debouce to only refresh once if multiple changes happen in quick succession
      .subscribe(date => this.refreshTimeStatusForDate(date));
  }

  /** Fetches the status for a single date without updating state */
  private async getTimeStatusForDate(date: DateTimeType, userId: number): Promise<DailyTimeStatus | null> {
    try {
      const [hasReturnedTime, hasUnsubmittedTime] = await Promise.all([
        this.timeService.checkHasReturnedTimeForDate(userId, date),
        this.timeService.checkHasUnsubmittedTimeForDate(userId, date),
      ]);

      return { date: dayjs(date), hasReturnedTime, hasUnsubmittedTime };
    } catch (err) {
      this.logService.error("AppUserTime.GetDailyTimeStatusFailed", err);
      return null;
    }
  }

  /** Updates the status for a single date */
  private async refreshTimeStatusForDate(targetDate: DateTimeType): Promise<void> {
    const userId = this.appUserService.currentUserId;
    if (!userId) return;

    const reviewPeriod = this.currentReviewPeriodService.value;
    if (!reviewPeriod || !reviewPeriod.getDatesInReviewPeriod(true).some(date => DateTimeHelper.checkOnSameDay(date, targetDate))) {
      return;
    }

    const newStatus = await this.getTimeStatusForDate(targetDate, userId);
    if (!newStatus) return;

    const currentStatus = this.dailyTimeStatusesSubject.getValue();
    const idx = currentStatus.findIndex(status => DateTimeHelper.checkOnSameDay(status.date, targetDate));

    // Recreate the array with the new status
    const next = [...currentStatus];
    if (idx >= 0) {
      next[idx] = newStatus;
    } else {
      next.push(newStatus);
    }
    this.emitIfChanged(next);
  }

  /** Rebuilds the entire list of DailyTimeStatuses for the current review period */
  private async refreshAllDailyTimeStatuses(): Promise<void> {
    const userId = this.appUserService.currentUserId;
    const reviewPeriod = this.currentReviewPeriodService.value;
    if (!userId || !reviewPeriod) {
      this.resetDailyTimeStatuses();
      return;
    }

    try {
      const dates = reviewPeriod.getDatesInReviewPeriod(true);
      const statuses = await Promise.all(
        dates.map(async date => this.getTimeStatusForDate(date, userId))
      );

      const validStatuses = statuses.filter((status): status is DailyTimeStatus => status !== null);
      if (validStatuses.length > 0) {
        this.emitIfChanged(validStatuses);
      }
    } catch (err) {
      this.logService.error("AppUserTime.RefreshAllDailyTimeStatusesFailed", err);
      this.resetDailyTimeStatuses();
    }
  }

  /** Only emits when the new array differs from the current one */
  private emitIfChanged(newList: DailyTimeStatus[]): void {
    const sorted = this.sortByDate(newList);
    const oldList = this.dailyTimeStatusesSubject.getValue();
    if (!this.checkDailyTimeStatusArraysEqual(oldList, sorted)) {
      this.dailyTimeStatusesSubject.next(sorted);
    }
  }

  /** Sorts the statuses by date */
  private sortByDate(list: DailyTimeStatus[]): DailyTimeStatus[] {
    return [...list].sort((a, b) =>
      a.date.valueOf() - b.date.valueOf()
    );
  }

  /** Compares two sorted DailyTimeStatus arrays for equality */
  private checkDailyTimeStatusArraysEqual(a: DailyTimeStatus[], b: DailyTimeStatus[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((statusItem, idx) =>
      DateTimeHelper.checkOnSameDay(statusItem.date, b[idx].date) &&
      statusItem.hasReturnedTime === b[idx].hasReturnedTime &&
      statusItem.hasUnsubmittedTime === b[idx].hasUnsubmittedTime
    );
  }

  /** Clears the current list */
  private resetDailyTimeStatuses(): void {
    this.emitIfChanged([]);
  }
}